import type { OrganizerUser } from 'modules/roles-manager/domain/interfaces/organizer-user';
import React, { createContext, useCallback, useContext, useRef, useState } from 'react';

type PagesCache = Record<number, OrganizerUser[]>;

type UsersPagesCacheContextValue = {
  pagesCache: PagesCache;
  setPageData: (page: number, data: OrganizerUser[]) => void;
  resetPagesCache: () => void;
  pageCount: number;
  setPageCount: (value: number) => void;
};

const UsersPagesCacheContext = createContext<UsersPagesCacheContextValue | undefined>(undefined);

export const UsersPagesCacheProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const cacheRef = useRef<PagesCache>({});
  const [pageCount, setPageCount] = useState(1);

  const setPageData = useCallback((page: number, data: OrganizerUser[]) => {
    cacheRef.current[page] = data;
  }, []);

  const resetPagesCache = useCallback(() => {
    cacheRef.current = {};
    setPageCount(1);
  }, []);

  return (
    <UsersPagesCacheContext.Provider
      value={{
        pagesCache: cacheRef.current,
        setPageData,
        resetPagesCache,
        pageCount,
        setPageCount,
      }}
    >
      {children}
    </UsersPagesCacheContext.Provider>
  );
};

export function useUsersPagesCache() {
  const ctx = useContext(UsersPagesCacheContext);
  if (!ctx) {
    throw new Error('useUsersPagesCache must be used within UsersPagesCacheProvider');
  }
  return ctx;
}
